import React, { useEffect, useState, useRef } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Upload, Trash2, Loader2, Image as ImageIcon, Palette, Building, Mail, Package, Tag, Plus, X, Check, Edit3 } from 'lucide-react';
import api from '@/utils/api';
import { showSuccess, showError } from '@/utils/toast';
import { useAuth } from '@/context/AuthContext';
import { useBranding } from '@/context/BrandingContext';
import ProductManager from '@/components/settings/ProductManager';
import LeadSourceManager from '@/components/settings/LeadSourceManager';

const Settings = () => {
  const { user } = useAuth();
  const { loadFullBranding, getLogoUrl, branding: globalBranding } = useBranding();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('company');

  const [branding, setBranding] = useState({
    company_name: '',
    tagline: '',
    address: '',
    email: '',
    phone: '',
    website: '',
    logo_url: '',
    primary_color: '#D4AF37',
    secondary_color: '#0F172A',
    sidebar_color: '#FFFFFF',
    dashboard_color: '#F9FAFB',
  });

  const [smtp, setSmtp] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    sender_name: '',
    sender_email: '',
    reply_to_email: '',
    smtp_enabled: false,
  });

  const isSuperAdmin = user?.role === 'super_admin';
  const canSeeSmtp = isSuperAdmin || user?.role === 'admin';

  useEffect(() => {
    fetchBranding();
    if (canSeeSmtp) fetchSmtp();
    // We intentionally run once after role resolves; downstream functions
    // read their own state via closures and the API URL is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeSmtp]);

  const fetchSmtp = async () => {
    try {
      const response = await api.get('/settings/smtp');
      setSmtp((prev) => ({ ...prev, ...response.data }));
    } catch (error) {
      // Non-admins receive 403 here; ignore but log for observability
      if (error?.response?.status !== 403) {
        console.warn('Failed to fetch SMTP settings:', error?.message);
      }
    }
  };

  const handleSaveSmtp = async () => {
    setSaving(true);
    try {
      const data = { ...smtp };
      if (data.smtp_password === '********') delete data.smtp_password;
      await api.put('/settings/smtp', data);
      showSuccess('SMTP settings saved successfully');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to save SMTP settings');
    } finally {
      setSaving(false);
    }
  };

  const fetchBranding = async () => {
    try {
      const response = await api.get('/settings/branding');
      setBranding({ ...branding, ...response.data });
    } catch (error) {
      showError('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      showError('Only PNG, JPG, and SVG files are allowed');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setBranding({ ...branding, logo_url: response.data.logo_url });
      showSuccess('Logo uploaded successfully');
      await loadFullBranding();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('Are you sure you want to remove the logo?')) return;

    try {
      await api.delete('/settings/logo');
      setBranding({ ...branding, logo_url: '' });
      showSuccess('Logo removed successfully');
      await loadFullBranding();
    } catch (error) {
      showError('Failed to remove logo');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/branding', branding);
      showSuccess('Branding settings saved successfully');
      await loadFullBranding();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const getDisplayLogoUrl = () => {
    if (branding.logo_url) {
      if (branding.logo_url.startsWith('http')) return branding.logo_url;
      return `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`;
    }
    return '';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Settings</h1>
          <p className="text-muted-foreground">Manage company branding and system settings</p>
          {!isSuperAdmin && (
            <div className="mt-4 p-4 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
              Only Super Admin can modify these settings. You are viewing in read-only mode.
            </div>
          )}
        </div>

        <div className="flex gap-2 mb-6 border-b border-border">
          <button
            data-testid="tab-company"
            onClick={() => setActiveTab('company')}
            className={`px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'company' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Building className="inline mr-2" size={18} />
            Company Branding
          </button>
          <button
            data-testid="tab-theme"
            onClick={() => setActiveTab('theme')}
            className={`px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'theme' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Palette className="inline mr-2" size={18} />
            Theme Colors
          </button>
          <button
            data-testid="tab-smtp"
            onClick={() => setActiveTab('smtp')}
            className={`px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'smtp' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Mail className="inline mr-2" size={18} />
            SMTP Email
          </button>
          <button
            data-testid="tab-products"
            onClick={() => setActiveTab('products')}
            className={`px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'products' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Package className="inline mr-2" size={18} />
            Products
          </button>
          <button
            data-testid="tab-sources"
            onClick={() => setActiveTab('sources')}
            className={`px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'sources' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Tag className="inline mr-2" size={18} />
            Lead Sources
          </button>
        </div>

        {activeTab === 'company' && (
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <ImageIcon size={20} />
                Company Logo
              </h2>
              <div className="flex items-start gap-6">
                <div className="w-32 h-32 rounded-xl border-2 border-dashed border-border bg-muted flex items-center justify-center overflow-hidden">
                  {getDisplayLogoUrl() ? (
                    <img src={getDisplayLogoUrl()} alt="Logo" className="w-full h-full object-contain p-2" />
                  ) : (
                    <ImageIcon className="text-muted-foreground" size={48} />
                  )}
                </div>
                <div className="flex-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                    data-testid="logo-upload-input"
                  />
                  <div className="flex flex-wrap gap-3">
                    <button
                      data-testid="upload-logo-button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={!isSuperAdmin || uploading}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
                    >
                      {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />}
                      {uploading ? 'Uploading...' : 'Upload Logo'}
                    </button>
                    {branding.logo_url && (
                      <button
                        data-testid="remove-logo-button"
                        onClick={handleRemoveLogo}
                        disabled={!isSuperAdmin}
                        className="flex items-center gap-2 px-4 py-2 border border-destructive text-destructive rounded-lg hover:bg-destructive/10 disabled:opacity-50 transition-colors"
                      >
                        <Trash2 size={18} />
                        Remove
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">Supported: PNG, JPG, SVG (max 5MB)</p>
                  <p className="text-xs text-muted-foreground mt-1">Logo will appear in login page, sidebar, dashboard, and reports.</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Company Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Company Name</label>
                  <input data-testid="company-name-input" type="text" disabled={!isSuperAdmin} value={branding.company_name || ''} onChange={(e) => setBranding({ ...branding, company_name: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Tagline</label>
                  <input type="text" disabled={!isSuperAdmin} value={branding.tagline || ''} onChange={(e) => setBranding({ ...branding, tagline: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Address</label>
                  <input type="text" disabled={!isSuperAdmin} value={branding.address || ''} onChange={(e) => setBranding({ ...branding, address: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input type="email" disabled={!isSuperAdmin} value={branding.email || ''} onChange={(e) => setBranding({ ...branding, email: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone Number</label>
                  <input type="tel" disabled={!isSuperAdmin} value={branding.phone || ''} onChange={(e) => setBranding({ ...branding, phone: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Website</label>
                  <input type="url" disabled={!isSuperAdmin} value={branding.website || ''} onChange={(e) => setBranding({ ...branding, website: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'theme' && (
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="text-xl font-semibold text-foreground mb-4">Theme Colors</h2>
            <p className="text-sm text-muted-foreground mb-6">Customize the appearance of your CRM</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">Primary Color</label>
                <div className="flex items-center gap-3">
                  <input data-testid="primary-color-input" type="color" disabled={!isSuperAdmin} value={branding.primary_color} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} className="w-16 h-12 rounded-lg border border-border cursor-pointer disabled:cursor-not-allowed" />
                  <input type="text" disabled={!isSuperAdmin} value={branding.primary_color} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} className="flex-1 px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Used for buttons, links, and primary actions</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Secondary Color</label>
                <div className="flex items-center gap-3">
                  <input type="color" disabled={!isSuperAdmin} value={branding.secondary_color} onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })} className="w-16 h-12 rounded-lg border border-border cursor-pointer disabled:cursor-not-allowed" />
                  <input type="text" disabled={!isSuperAdmin} value={branding.secondary_color} onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })} className="flex-1 px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Used for secondary elements</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Sidebar Color</label>
                <div className="flex items-center gap-3">
                  <input type="color" disabled={!isSuperAdmin} value={branding.sidebar_color} onChange={(e) => setBranding({ ...branding, sidebar_color: e.target.value })} className="w-16 h-12 rounded-lg border border-border cursor-pointer disabled:cursor-not-allowed" />
                  <input type="text" disabled={!isSuperAdmin} value={branding.sidebar_color} onChange={(e) => setBranding({ ...branding, sidebar_color: e.target.value })} className="flex-1 px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Dashboard Color</label>
                <div className="flex items-center gap-3">
                  <input type="color" disabled={!isSuperAdmin} value={branding.dashboard_color} onChange={(e) => setBranding({ ...branding, dashboard_color: e.target.value })} className="w-16 h-12 rounded-lg border border-border cursor-pointer disabled:cursor-not-allowed" />
                  <input type="text" disabled={!isSuperAdmin} value={branding.dashboard_color} onChange={(e) => setBranding({ ...branding, dashboard_color: e.target.value })} className="flex-1 px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'smtp' && (
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="text-xl font-semibold text-foreground mb-2">SMTP Email Configuration</h2>
            <p className="text-sm text-muted-foreground mb-6">Configure email server settings for sending notifications</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">SMTP Host</label>
                <input data-testid="smtp-host" type="text" placeholder="smtp.gmail.com" disabled={!isSuperAdmin} value={smtp.smtp_host || ''} onChange={(e) => setSmtp({ ...smtp, smtp_host: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">SMTP Port</label>
                <input data-testid="smtp-port" type="number" placeholder="587" disabled={!isSuperAdmin} value={smtp.smtp_port || ''} onChange={(e) => setSmtp({ ...smtp, smtp_port: parseInt(e.target.value) || 587 })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">SMTP Username</label>
                <input type="text" disabled={!isSuperAdmin} value={smtp.smtp_username || ''} onChange={(e) => setSmtp({ ...smtp, smtp_username: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">SMTP Password</label>
                <input type="password" placeholder="********" disabled={!isSuperAdmin} value={smtp.smtp_password || ''} onChange={(e) => setSmtp({ ...smtp, smtp_password: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Sender Name</label>
                <input type="text" placeholder="Goldpeak Financial" disabled={!isSuperAdmin} value={smtp.sender_name || ''} onChange={(e) => setSmtp({ ...smtp, sender_name: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Sender Email</label>
                <input type="email" placeholder="noreply@goldpeak.com" disabled={!isSuperAdmin} value={smtp.sender_email || ''} onChange={(e) => setSmtp({ ...smtp, sender_email: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Reply-To Email</label>
                <input type="email" placeholder="support@goldpeak.com" disabled={!isSuperAdmin} value={smtp.reply_to_email || ''} onChange={(e) => setSmtp({ ...smtp, reply_to_email: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input data-testid="smtp-enabled" type="checkbox" disabled={!isSuperAdmin} checked={smtp.smtp_enabled || false} onChange={(e) => setSmtp({ ...smtp, smtp_enabled: e.target.checked })} className="w-5 h-5 rounded border-border" />
                  <span className="text-sm font-medium">Enable Email Notifications</span>
                </label>
                <p className="text-xs text-muted-foreground mt-1 ml-8">Sends automated emails for lead acknowledgement, follow-ups, document requests, sanction letters, etc.</p>
              </div>
            </div>
            {isSuperAdmin && (
              <div className="mt-6 flex justify-end">
                <button
                  data-testid="save-smtp-button"
                  onClick={handleSaveSmtp}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
                >
                  {saving && <Loader2 className="animate-spin" size={18} />}
                  Save SMTP Settings
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'products' && (
          <div className="bg-card border border-border rounded-xl p-6">
            <ProductManager readOnly={!isSuperAdmin && user?.role !== 'admin'} />
          </div>
        )}

        {activeTab === 'sources' && (
          <div className="bg-card border border-border rounded-xl p-6">
            <LeadSourceManager readOnly={!isSuperAdmin && user?.role !== 'admin'} />
          </div>
        )}

        {isSuperAdmin && activeTab !== 'smtp' && activeTab !== 'products' && activeTab !== 'sources' && (
          <div className="mt-6 flex justify-end">
            <button
              data-testid="save-settings-button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {saving && <Loader2 className="animate-spin" size={18} />}
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Settings;
